import BRAND from './brand';

// ---------------------------------------------------------------------------
// What search engines are told, in one place.
//
// THE SHAPE OF EVERY TITLE is "<what this page is> | SellerList AI". The brand
// goes LAST, because a result list shows the first ~60 characters and the
// useful half is what the page does — somebody searching "meesho catalog
// upload file" is not searching for us by name, and a title that opens with
// the brand spends its best characters on a word they have never heard.
//
// THE KEYWORDS ARE THE SEARCHES A REAL SELLER TYPES. Not "AI SaaS platform"
// but "how to upload catalog on meesho" — the phrasing of somebody with a
// spreadsheet open and a deadline. Every one of these appears in the visible
// copy of the page it belongs to; a description promising something the page
// does not say is how a listing earns a click and loses the visitor.
//
// AND NOTHING HERE OVER-CLAIMS. Meesho is the one marketplace a file can be
// produced for today, so every description says Meesho and calls the rest
// coming soon. Ranking for "amazon listing tool" and then failing to produce
// an Amazon file is worse than not ranking for it.
// ---------------------------------------------------------------------------

export const SITE = BRAND.site;

// Used when a page does not name its own.
export const DEFAULT_TITLE = `${BRAND.name} — Meesho Catalog Upload File Generator for Indian Sellers`;

// Google shows roughly 155 characters. Anything past that is written for
// nobody: it is cut mid-sentence in the only place a searcher reads it.
export const DEFAULT_DESCRIPTION =
  'Turn one product into a Meesho-ready listing in minutes. AI writes the title '
  + 'and description, prepares the images, and fills every required field. Free to '
  + 'start, no card needed.';

// Google ignores this tag. It is kept because other crawlers and several
// Indian marketplaces' own link previewers still read it, and it costs a line.
export const KEYWORDS = [
  'meesho catalog upload file',
  'meesho product listing',
  'meesho seller tools',
  'bulk product upload meesho',
  'product listing software india',
  'ecommerce catalog management india',
  'ai product description generator',
  'product title generator for sellers',
  'marketplace listing file generator',
  'online seller tools india',
  'catalog file for meesho seller panel',
  'sellerlist ai'
].join(', ');

/**
 * Per-page metadata.
 *
 * Every entry is written for the search that should find it, and every one is
 * honest about what the page contains.
 */
export const PAGE_SEO = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION
  },
  '/pricing': {
    title: 'Pricing & Credits — Pay Per Listing | SellerList AI',
    description:
      'Plans from ₹499/month, credit packs from ₹199. One completed listing costs 10 '
      + 'credits, and preparing one is always free — you pay only when it is finished.'
  },
  '/demo': {
    title: 'See a Demo — Watch One Product Become a Meesho Listing | SellerList AI',
    description:
      'A walkthrough of the whole thing: add a product, let the AI write the title and '
      + 'description, prepare the images, and download a Meesho-ready upload file. No signup '
      + 'needed to watch.'
  },
  '/support': {
    title: 'Support & Help Centre for Sellers | SellerList AI',
    description:
      'Answers about listings, credits, exports and marketplace files — and a way to reach '
      + 'a person. Email support answered by a human, usually the same day.'
  },
  '/about': {
    title: 'About Us — Built for Indian Marketplace Sellers | SellerList AI',
    description:
      'SellerList AI is a cataloguing tool for Indian marketplace sellers, built by '
      + 'Spaweval Technologies. We prepare the part of selling that eats the day: titles, '
      + 'descriptions, images and the upload file.'
  },
  '/contact': {
    title: 'Contact Us | SellerList AI',
    // Interpolated, not typed out: a test fails when an address is hardcoded,
    // and it is right to — the last rename left old addresses in six files.
    description:
      `Questions about your account go to ${BRAND.email.support}. Anything else — general `
      + `questions, partnerships, supplying products — write to ${BRAND.email.info}.`
  },
  '/register': {
    title: 'Create a Free Seller Account | SellerList AI',
    description:
      'Start free with credits included and no card required. Add a product, let the AI '
      + 'prepare it, and download a Meesho-ready file the same day.'
  },
  '/login': {
    title: 'Seller Login | SellerList AI',
    description: 'Sign in to your SellerList AI account to prepare listings and download marketplace files.'
  },
  '/vendor/register': {
    title: 'Apply as a Supplier — List Your Products for Sellers | SellerList AI',
    description:
      'Suppliers and wholesalers: put your catalogue in front of sellers who list on Indian '
      + 'marketplaces. Apply to become a verified supplier on SellerList AI.'
  },
  '/privacy': { title: 'Privacy Policy | SellerList AI', description: 'How SellerList AI collects, uses and protects seller data, and the rights you have over it.' },
  '/terms': { title: 'Terms & Conditions | SellerList AI', description: 'The terms that apply to using SellerList AI, including accounts, credits, payments and acceptable use.' },
  '/cookies': { title: 'Cookie Policy | SellerList AI', description: 'Which cookies SellerList AI sets, what each one does, and how to turn the optional ones off.' },
  '/refunds': { title: 'Refund Policy | SellerList AI', description: 'When credits and plan payments can be refunded on SellerList AI, and how to ask.' },
  '/disclaimer': { title: 'Disclaimer | SellerList AI', description: 'What SellerList AI does and does not guarantee about marketplace listings, approvals and outcomes.' }
};

/** Everything a crawler should be able to reach. Drives the sitemap. */
export const PUBLIC_ROUTES = Object.keys(PAGE_SEO);

/**
 * The organisation, for a knowledge panel and for anything that reads
 * structured data about who publishes this site.
 */
export const organisationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  // A STABLE IDENTITY, ANCHORED TO THIS DOMAIN.
  //
  // The product used to live at another address, and a search for the brand
  // name still surfaces it. An @id every other block on the site points at is
  // how a search engine is told these pages describe one thing, and that the
  // thing lives here. The 301 from the old domain does the heavy lifting; this
  // is what the signals land on.
  '@id': `${SITE}/#organization`,
  name: BRAND.name,
  // The ways people actually type it. Without these, "SellerList" and
  // "Sellerlist AI" are three different strings as far as a matcher is
  // concerned.
  alternateName: ['SellerList', 'Sellerlist AI', 'SellerList AI India'],
  legalName: BRAND.legalEntity,
  url: SITE,
  logo: `${SITE}/favicon-512.png`,
  email: BRAND.email.info,
  address: { '@type': 'PostalAddress', addressCountry: 'IN' },
  contactPoint: [{
    '@type': 'ContactPoint',
    contactType: 'customer support',
    email: BRAND.email.support,
    areaServed: 'IN',
    availableLanguage: ['en', 'hi']
  }]
});

/**
 * The product itself.
 *
 * `SoftwareApplication` with an offer is what lets a result carry a price, and
 * the price quoted is the real starting price rather than a marketing number.
 */
export const applicationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: BRAND.name,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  description: DEFAULT_DESCRIPTION,
  url: SITE,
  // By reference, so the publisher IS the organisation above rather than a
  // second, unrelated one that happens to share a name.
  publisher: { '@id': `${SITE}/#organization` },
  offers: {
    '@type': 'Offer',
    price: '199',
    priceCurrency: 'INR',
    description: 'Credit packs from ₹199. Free to start, no card required.',
    url: `${SITE}/pricing`
  },
  featureList: [
    'AI-written product titles and descriptions',
    'Marketplace-ready product images',
    'Meesho catalog upload file',
    'Bulk product cataloguing',
    'Supplier catalogue to import products from'
  ]
});

/** Turns the FAQ already on the page into something Google can show. */
export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: (faqs || []).map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a }
  }))
});
